import { useEffect, useState } from 'react'
import { logoCache } from '../lib/logoCache'
import type { Place } from '../types/display'

/** Resolves the merchant's own logo, and nothing else.
 *
 *  The slot stays empty until that logo has loaded and decoded. There is no
 *  generic POS logo to fall back on: a place with no logo, or whose logo fails
 *  to load, shows an empty frame rather than someone else's branding on the
 *  merchant's screen. `src` is therefore null until the shop's logo is ready,
 *  and may stay null forever. */
export function useShopLogo(placeId: string, place: Place | null, useCache = true): { src: string | null } {
  const [src, setSrc] = useState<string | null>(null)

  // Paint the logo this place used last time, before the place record arrives.
  // Not for a device-request session: its branding is only shown once the API
  // has confirmed the grant, and `place_id` in the URL proves nothing.
  useEffect(() => {
    if (!useCache) return
    const cached = logoCache.read(placeId)
    if (!cached) return

    let current = true
    void logoCache.preload(cached).then((ok) => {
      if (current && ok) setSrc((shown) => shown ?? cached)
    })
    return () => {
      current = false
    }
  }, [placeId, useCache])

  // Once the place is known, show its logo if it has one.
  useEffect(() => {
    if (!place) {
      // Without the cache, null means the grant was refused: nothing of this
      // shop stays on screen. With it, null only means not known yet.
      if (useCache) return
      let current = true
      void Promise.resolve().then(() => {
        if (current) setSrc(null)
      })
      return () => {
        current = false
      }
    }

    let current = true
    const url = place.logo

    if (!url) {
      // The place record is authoritative: it has no logo, so drop the stale
      // cache and leave the slot empty.
      logoCache.clear(placeId)
      void Promise.resolve().then(() => {
        if (current) setSrc(null)
      })
      return () => {
        current = false
      }
    }

    void logoCache.preload(url).then((ok) => {
      if (!current) return
      if (ok) {
        logoCache.write(placeId, url)
        setSrc(url)
      } else {
        // A logo that will not load must not evict one already on screen.
        logoCache.clear(placeId)
      }
    })

    return () => {
      current = false
    }
  }, [placeId, place, useCache])

  return { src }
}
